import { apiService } from '../../../../../components/api/apiService';
import { logMcp } from './mcpLogger';
import type {
  AuthType,
  ConnectionFormValues,
  ConnectionSource,
  ConnectionStatus,
  McpCatalogEntry,
  McpDiscoveredResource,
  McpDiscoveredTool,
  McpInvokeResult,
  McpServerConnection,
  McpTestResult,
  Transport,
} from './types';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/mcp-connections';
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
export const REMOTE_URL_ERROR = 'Remote MCP URL must be http or https';

function isLocalHost(host: string): boolean {
  const name = host.trim().toLowerCase().replace(/^\[|\]$/g, '');
  return LOCAL_HOSTS.has(name) || name.endsWith('.local');
}

export function normalizeRemoteMcpUrl(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return value;
  const candidate = `http://${value}`;
  try {
    const parsed = new URL(candidate);
    if (isLocalHost(parsed.hostname)) {
      logMcp('app', 'normalized_local_mcp_url', { host: parsed.hostname });
      return candidate;
    }
  } catch {
    return value;
  }
  return value;
}

export function isValidRemoteMcpUrl(raw: string): boolean {
  const normalized = normalizeRemoteMcpUrl(raw);
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function formatErrorDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return '';
  return detail
    .map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return '';
      const rec = item as { msg?: unknown; loc?: unknown };
      const msg = rec.msg == null ? '' : String(rec.msg);
      if (!msg) return '';
      if (Array.isArray(rec.loc) && rec.loc.length) {
        const loc = rec.loc.filter((part) => part !== 'body').join('.');
        return loc ? `${loc}: ${msg}` : msg;
      }
      return msg;
    })
    .filter(Boolean)
    .join(', ');
}

function errorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string; detail?: unknown }; message?: string }; status?: number };
    message?: string;
  };
  const code = axiosErr.code || '';
  const raw = axiosErr.message || '';
  if (code === 'ERR_NETWORK' || raw === 'Network Error' || /ECONNREFUSED/i.test(raw)) {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  const detailText = formatErrorDetail(axiosErr.response?.data?.error?.detail);
  if (detailText) {
    return `${axiosErr.response?.data?.error?.message || 'Request failed'}: ${detailText}`;
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'MCP request failed'
  );
}

function asString(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value);
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function asStringMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const name = key.trim();
    if (!name) continue;
    out[name] = raw == null ? '' : String(raw);
  }
  return out;
}

function asTransport(value: unknown): Transport {
  if (value === 'sse' || value === 'streamable-http' || value === 'stdio') return value;
  return 'stdio';
}

function asAuth(value: unknown): AuthType {
  if (value === 'bearer' || value === 'apiKey' || value === 'header' || value === 'none') return value;
  return 'none';
}

function asStatus(value: unknown): ConnectionStatus {
  if (value === 'testing' || value === 'connected' || value === 'failed' || value === 'never_tested') return value;
  return 'never_tested';
}

function asSource(value: unknown): ConnectionSource {
  return value === 'catalog' ? 'catalog' : 'custom';
}

function mapTool(raw: Record<string, unknown>): McpDiscoveredTool {
  const schema = raw.inputSchema ?? raw.input_schema;
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asString(raw.description),
    inputSchemaSummary: asString(raw.inputSchemaSummary ?? raw.input_schema_summary),
    inputSchema: schema && typeof schema === 'object' && !Array.isArray(schema) ? (schema as Record<string, unknown>) : null,
    enabled: asBool(raw.enabled),
  };
}

function mapResource(raw: Record<string, unknown>): McpDiscoveredResource {
  return {
    id: asString(raw.id),
    uri: asString(raw.uri),
    name: asString(raw.name),
    mimeType: asString(raw.mimeType ?? raw.mime_type, 'application/octet-stream'),
    description: asString(raw.description) || undefined,
    enabled: asBool(raw.enabled),
  };
}

function mapConnection(raw: Record<string, unknown>): McpServerConnection {
  const tools = Array.isArray(raw.tools) ? raw.tools.map((item) => mapTool(item as Record<string, unknown>)) : [];
  const resources = Array.isArray(raw.resources)
    ? raw.resources.map((item) => mapResource(item as Record<string, unknown>))
    : [];
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asString(raw.description) || undefined,
    transport: asTransport(raw.transport),
    command: asString(raw.command) || undefined,
    args: asStringArray(raw.args),
    env: asStringMap(raw.env),
    url: asString(raw.url) || undefined,
    headers: asStringMap(raw.headers),
    authType: asAuth(raw.authType ?? raw.auth_type),
    authConfigured: asBool(raw.authConfigured ?? raw.auth_configured),
    tags: asStringArray(raw.tags),
    isActive: asBool(raw.isActive ?? raw.is_active, true),
    source: asSource(raw.source),
    catalogId: asString(raw.catalogId ?? raw.catalog_id) || undefined,
    code: asString(raw.code) || undefined,
    status: asStatus(raw.status),
    lastTestedAt: asString(raw.lastTestedAt ?? raw.last_tested_at) || undefined,
    lastError: asString(raw.lastError ?? raw.last_error) || undefined,
    tools,
    resources,
    createdAt: asString(raw.createdAt ?? raw.created_at),
    updatedAt: asString(raw.updatedAt ?? raw.updated_at),
  };
}

function mapCatalog(raw: Record<string, unknown>): McpCatalogEntry {
  const cfg = (raw.defaultConfig ?? raw.default_config ?? {}) as Record<string, unknown>;
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asString(raw.description),
    publisher: asString(raw.publisher),
    repoUrl: asString(raw.repoUrl ?? raw.repo_url),
    transport: asTransport(raw.transport),
    code: asString(raw.code),
    defaultConfig: {
      transport: asTransport(cfg.transport ?? raw.transport),
      command: asString(cfg.command),
      args: asStringArray(cfg.args),
      env: asStringMap(cfg.env),
      url: asString(cfg.url),
      headers: asStringMap(cfg.headers),
      authType: asAuth(cfg.authType ?? cfg.auth_type),
      authValue: asString(cfg.authValue ?? cfg.auth_value),
      tags: asStringArray(cfg.tags),
    },
    toolCount: asNumber(raw.toolCount ?? raw.tool_count, 0),
    resourceCount: asNumber(raw.resourceCount ?? raw.resource_count, 0),
    tags: asStringArray(raw.tags),
  };
}

function mapTest(raw: Record<string, unknown>): McpTestResult {
  const tools = Array.isArray(raw.tools) ? raw.tools.map((item) => mapTool(item as Record<string, unknown>)) : [];
  const resources = Array.isArray(raw.resources)
    ? raw.resources.map((item) => mapResource(item as Record<string, unknown>))
    : [];
  const connectionRaw = raw.connection;
  return {
    success: asBool(raw.success),
    message: asString(raw.message),
    latencyMs: asNumber(raw.latencyMs ?? raw.latency_ms, 0),
    toolCount: asNumber(raw.toolCount ?? raw.tool_count, tools.length),
    resourceCount: asNumber(raw.resourceCount ?? raw.resource_count, resources.length),
    tools,
    resources,
    lastError: asString(raw.lastError ?? raw.last_error),
    connection:
      connectionRaw && typeof connectionRaw === 'object'
        ? mapConnection(connectionRaw as Record<string, unknown>)
        : null,
  };
}

function parseJsonObject(text: string): Record<string, string> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return asStringMap(parsed);
    }
  } catch {
    /* fall through to KEY=value */
  }
  const out: Record<string, string> = {};
  for (const line of trimmed.split('\n')) {
    const idx = line.indexOf('=');
    if (idx <= 0) continue;
    out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  if (Object.keys(out).length === 0) {
    logMcp('warning', 'invalid_json_map');
    throw new Error('Headers and environment must be a JSON object or KEY=value lines');
  }
  return out;
}

function parseArgs(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const out: string[] = [];
  for (const line of lines) {
    if (line.startsWith('--') && line.includes('=')) {
      out.push(line);
      continue;
    }
    if (line.includes(',') && !/\s/.test(line)) {
      out.push(...line.split(',').map((s) => s.trim()).filter(Boolean));
      continue;
    }
    out.push(line);
  }
  return out;
}

function parseTags(text: string): string[] {
  return text
    .split(/,|\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function emptyFormValues(): ConnectionFormValues {
  return {
    name: '',
    description: '',
    transport: 'stdio',
    command: 'npx',
    argsText: '',
    envText: '',
    url: '',
    headersText: '',
    authType: 'none',
    authValue: '',
    tagsText: '',
    isActive: true,
    source: 'custom',
    code: '',
  };
}

export function connectionToFormValues(conn: McpServerConnection): ConnectionFormValues {
  return {
    name: conn.name,
    description: conn.description ?? '',
    transport: conn.transport,
    command: conn.command ?? '',
    argsText: (conn.args ?? []).join('\n'),
    envText: conn.env && Object.keys(conn.env).length ? JSON.stringify(conn.env, null, 2) : '',
    url: conn.url ?? '',
    headersText:
      conn.headers && Object.keys(conn.headers).length ? JSON.stringify(conn.headers, null, 2) : '',
    authType: conn.authType,
    authValue: '',
    tagsText: conn.tags.join(', '),
    isActive: conn.isActive,
    source: conn.source,
    catalogId: conn.catalogId,
    code: conn.code ?? '',
  };
}

export function catalogToFormValues(entry: McpCatalogEntry): ConnectionFormValues {
  const cfg = entry.defaultConfig;
  return {
    name: entry.name,
    description: entry.description,
    transport: cfg.transport ?? entry.transport,
    command: cfg.command ?? 'npx',
    argsText: (cfg.args ?? []).join('\n'),
    envText: cfg.env && Object.keys(cfg.env).length ? JSON.stringify(cfg.env, null, 2) : '',
    url: cfg.url ?? '',
    headersText:
      cfg.headers && Object.keys(cfg.headers).length ? JSON.stringify(cfg.headers, null, 2) : '',
    authType: cfg.authType ?? 'none',
    authValue: cfg.authValue ?? '',
    tagsText: (cfg.tags ?? entry.tags).join(', '),
    isActive: true,
    source: 'catalog',
    catalogId: entry.id,
    code: entry.code,
  };
}

export function formValuesToPayload(values: ConnectionFormValues, discover = false): Record<string, unknown> {
  const isStdio = values.transport === 'stdio';
  return {
    name: values.name.trim(),
    description: values.description.trim(),
    transport: values.transport,
    command: isStdio ? values.command.trim() : '',
    args: isStdio ? parseArgs(values.argsText) : [],
    env: isStdio ? parseJsonObject(values.envText) : {},
    url: isStdio ? '' : normalizeRemoteMcpUrl(values.url),
    headers: isStdio ? {} : parseJsonObject(values.headersText),
    authType: values.authType,
    authValue: values.authType === 'none' ? '' : values.authValue.trim(),
    tags: parseTags(values.tagsText),
    isActive: values.isActive,
    source: values.source,
    catalogId: values.catalogId ?? '',
    code: values.code.trim().toUpperCase(),
    discover,
  };
}

async function discoverSavedConnection(id: string, kind: 'test' | 'refresh'): Promise<McpTestResult> {
  const startEvent = kind === 'refresh' ? 'refresh_tools' : 'test_connection';
  logMcp('app', startEvent, { id });
  try {
    const data = (await apiService.post(`${BASE}/${id}/test`, {}, CONNECTION)) as Record<string, unknown>;
    const result = mapTest(data);
    logMcp(result.success ? 'app' : 'warning', `${startEvent}_done`, {
      id,
      success: result.success,
      tools: result.toolCount,
    });
    return result;
  } catch (err) {
    const message = errorMessage(err);
    logMcp('error', `${startEvent}_failed`, { message, id });
    throw new Error(message);
  }
}

export const mcpConnectionService = {
  async listCatalog(): Promise<McpCatalogEntry[]> {
    logMcp('app', 'list_catalog');
    try {
      const data = (await apiService.get(`${BASE}/catalog`, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : [];
      const entries = items.map((item) => mapCatalog(item as Record<string, unknown>));
      logMcp('app', 'list_catalog_ok', { count: entries.length });
      return entries;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'list_catalog_failed', { message });
      throw new Error(message);
    }
  },

  async list(): Promise<McpServerConnection[]> {
    logMcp('app', 'list_connections');
    try {
      const data = (await apiService.get(BASE, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : [];
      const connections = items.map((item) => mapConnection(item as Record<string, unknown>));
      logMcp('app', 'list_connections_ok', { count: connections.length });
      return connections;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'list_connections_failed', { message });
      throw new Error(message);
    }
  },

  async getByCode(code: string): Promise<McpServerConnection> {
    const normalized = code.trim().toUpperCase();
    logMcp('app', 'get_connection_by_code', { code: normalized });
    try {
      const data = (await apiService.get(`${BASE}/by-code/${encodeURIComponent(normalized)}`, {}, CONNECTION)) as Record<
        string,
        unknown
      >;
      const connection = mapConnection(data);
      logMcp('app', 'get_connection_by_code_ok', { code: normalized, id: connection.id });
      return connection;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'get_connection_by_code_failed', { message, code: normalized });
      throw new Error(message);
    }
  },

  async get(id: string): Promise<McpServerConnection> {
    logMcp('app', 'get_connection', { id });
    try {
      const data = (await apiService.get(`${BASE}/${id}`, {}, CONNECTION)) as Record<string, unknown>;
      const connection = mapConnection(data);
      logMcp('app', 'get_connection_ok', { id, tools: connection.tools.length });
      return connection;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'get_connection_failed', { message, id });
      throw new Error(message);
    }
  },

  async create(values: ConnectionFormValues, discover = false): Promise<McpServerConnection> {
    logMcp('app', 'create_connection', { name: values.name, transport: values.transport, discover });
    try {
      const data = (await apiService.post(BASE, formValuesToPayload(values, discover), CONNECTION)) as Record<
        string,
        unknown
      >;
      const connection = mapConnection(data);
      logMcp('app', 'create_connection_ok', { id: connection.id, status: connection.status });
      return connection;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'create_connection_failed', { message, name: values.name });
      throw new Error(message);
    }
  },

  async update(id: string, values: ConnectionFormValues): Promise<McpServerConnection> {
    logMcp('app', 'update_connection', { id, name: values.name });
    try {
      const data = (await apiService.put(`${BASE}/${id}`, formValuesToPayload(values), CONNECTION)) as Record<
        string,
        unknown
      >;
      const connection = mapConnection(data);
      logMcp('app', 'update_connection_ok', { id });
      return connection;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'update_connection_failed', { message, id });
      throw new Error(message);
    }
  },

  async remove(id: string): Promise<void> {
    logMcp('app', 'delete_connection', { id });
    try {
      await apiService.delete(`${BASE}/${id}`, CONNECTION);
      logMcp('app', 'delete_connection_ok', { id });
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'delete_connection_failed', { message, id });
      throw new Error(message);
    }
  },

  async testDraft(values: ConnectionFormValues): Promise<McpTestResult> {
    const payload = formValuesToPayload(values);
    logMcp('app', 'test_draft', {
      name: values.name,
      transport: values.transport,
      argCount: Array.isArray(payload.args) ? payload.args.length : 0,
    });
    try {
      const data = (await apiService.post(`${BASE}/test-draft`, payload, CONNECTION)) as Record<
        string,
        unknown
      >;
      const result = mapTest(data);
      logMcp(result.success ? 'app' : 'warning', 'test_draft_done', {
        success: result.success,
        tools: result.toolCount,
        latencyMs: result.latencyMs,
      });
      return result;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'test_draft_failed', { message });
      throw new Error(message);
    }
  },

  async test(id: string): Promise<McpTestResult> {
    return discoverSavedConnection(id, 'test');
  },

  async refreshTools(id: string): Promise<McpTestResult> {
    return discoverSavedConnection(id, 'refresh');
  },

  async saveSelections(
    id: string,
    toolEnabled: Record<string, boolean>,
    resourceEnabled: Record<string, boolean>
  ): Promise<McpServerConnection> {
    logMcp('app', 'save_selections', { id });
    try {
      const data = (await apiService.put(
        `${BASE}/${id}/selections`,
        { toolEnabled, resourceEnabled },
        CONNECTION
      )) as Record<string, unknown>;
      const connection = mapConnection(data);
      logMcp('app', 'save_selections_ok', { id, tools: connection.tools.length });
      return connection;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'save_selections_failed', { message, id });
      throw new Error(message);
    }
  },

  async invokeTool(id: string, toolId: string, toolName: string, args: Record<string, unknown>): Promise<McpInvokeResult> {
    logMcp('app', 'invoke_tool', { id, toolName });
    try {
      const data = (await apiService.post(
        `${BASE}/${id}/tools/invoke`,
        { toolId, toolName, arguments: args },
        CONNECTION
      )) as Record<string, unknown>;
      const result: McpInvokeResult = {
        success: asBool(data.success),
        latencyMs: asNumber(data.latencyMs ?? data.latency_ms, 0),
        result: data.result,
        error: asString(data.error),
      };
      logMcp(result.success ? 'app' : 'warning', 'invoke_tool_done', {
        id,
        toolName,
        success: result.success,
        latencyMs: result.latencyMs,
      });
      return result;
    } catch (err) {
      const message = errorMessage(err);
      logMcp('error', 'invoke_tool_failed', { message, id, toolName });
      throw new Error(message);
    }
  },
};
